import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { X, Plus, ArrowLeft } from "lucide-react";
import { z } from 'zod';
import ImageUpload from "./ImageUpload";

// Validation schema for project data
const projectSchema = z.object({
  title: z.string().trim().min(1, 'Le titre est requis').max(100, 'Le titre doit faire moins de 100 caractères'),
  description: z.string().trim().min(1, 'La description est requise').max(500, 'La description doit faire moins de 500 caractères'),
  long_description: z.string().max(2000, 'La description longue doit faire moins de 2000 caractères').optional(),
  demo_url: z.string().url('URL de démo invalide').optional().or(z.literal('')),
  github_url: z.string().url('URL GitHub invalide').optional().or(z.literal('')),
  video_url: z.string().url('URL de vidéo invalide').optional().or(z.literal('')),
});

type Project = {
  id: string;
  title: string;
  description: string;
  long_description?: string;
  image?: string;
  tags: string[];
  demo_url?: string;
  github_url?: string;
  video_url?: string;
  is_school_project: boolean;
  color: 'yellow' | 'blue' | 'green' | 'pink';
  preview_images: string[];
  before_after_before?: string;
  before_after_after?: string;
  is_visible: boolean;
  display_order: number;
  display_type: 'carousel' | 'before_after' | 'video';
};

interface ProjectFormProps {
  project?: Project | null;
  onSuccess: () => void;
  onCancel: () => void;
  onBack?: () => void;
}

const ProjectForm = ({ project, onSuccess, onCancel, onBack }: ProjectFormProps) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    long_description: "",
    image: "",
    tags: [] as string[],
    demo_url: "",
    github_url: "",
    video_url: "",
    is_school_project: false,
    color: "blue" as 'yellow' | 'blue' | 'green' | 'pink',
    preview_images: [] as string[],
    before_after_before: "",
    before_after_after: "",
    is_visible: true,
    display_order: 0,
    display_type: "carousel" as 'carousel' | 'before_after' | 'video',
  });
  
  const [newTag, setNewTag] = useState("");
  const [newPreviewImage, setNewPreviewImage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title,
        description: project.description,
        long_description: project.long_description || "",
        image: project.image || "",
        tags: project.tags || [],
        demo_url: project.demo_url || "",
        github_url: project.github_url || "",
        video_url: project.video_url || "",
        is_school_project: project.is_school_project,
        color: project.color,
        preview_images: project.preview_images || [],
        before_after_before: project.before_after_before || "",
        before_after_after: project.before_after_after || "",
        is_visible: project.is_visible,
        display_order: project.display_order,
        display_type: project.display_type || "carousel",
      });
    }
  }, [project]);

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const addPreviewImage = () => {
    if (newPreviewImage.trim() && !formData.preview_images.includes(newPreviewImage.trim())) {
      setFormData({
        ...formData,
        preview_images: [...formData.preview_images, newPreviewImage.trim()]
      });
      setNewPreviewImage("");
    }
  };

  const removePreviewImage = (imageToRemove: string) => {
    setFormData({
      ...formData,
      preview_images: formData.preview_images.filter(img => img !== imageToRemove)
    });
  };

  const handleImageUpload = (type: 'main' | 'before' | 'after' | 'preview') => (url: string) => {
    if (type === 'main') {
      setFormData({ ...formData, image: url });
    } else if (type === 'before') {
      setFormData({ ...formData, before_after_before: url });
    } else if (type === 'after') {
      setFormData({ ...formData, before_after_after: url });
    } else if (type === 'preview') {
      setFormData({ ...formData, preview_images: [...formData.preview_images, url] });
    }
  };

  const handleImageRemove = (type: 'main' | 'before' | 'after') => () => {
    if (type === 'main') {
      setFormData({ ...formData, image: '' });
    } else if (type === 'before') {
      setFormData({ ...formData, before_after_before: '' });
    } else if (type === 'after') {
      setFormData({ ...formData, before_after_after: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationErrors({});

    try {
      // Validate form data
      const validation = projectSchema.safeParse({
        title: formData.title.trim(),
        description: formData.description.trim(),
        long_description: formData.long_description.trim(),
        demo_url: formData.demo_url.trim(),
        github_url: formData.github_url.trim(),
        video_url: formData.video_url.trim(),
      });

      if (!validation.success) {
        const fieldErrors: Record<string, string> = {};
        validation.error.issues.forEach(err => {
          const field = err.path[0] as string;
          fieldErrors[field] = err.message;
        });
        setValidationErrors(fieldErrors);
        toast({
          title: "Erreur de validation",
          description: "Veuillez corriger les erreurs dans le formulaire",
          variant: "destructive",
        });
        return;
      }

      const dataToSave = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        long_description: formData.long_description.trim() || null,
        image: formData.image.trim() || null,
        tags: formData.tags.filter(tag => tag.trim().length > 0).map(tag => tag.trim()),
        demo_url: formData.demo_url.trim() || null,
        github_url: formData.github_url.trim() || null,
        video_url: formData.video_url.trim() || null,
        is_school_project: formData.is_school_project,
        color: formData.color,
        preview_images: formData.preview_images.filter(img => img.trim().length > 0),
        before_after_before: formData.before_after_before.trim() || null,
        before_after_after: formData.before_after_after.trim() || null,
        is_visible: formData.is_visible,
        display_order: formData.display_order,
        display_type: formData.display_type,
      };

      if (project) {
        // Update existing project
        const { error } = await supabase
          .from("projects")
          .update(dataToSave)
          .eq("id", project.id);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Projet mis à jour avec succès",
        });
      } else {
        // Create new project
        const { error } = await supabase
          .from("projects")
          .insert([dataToSave]);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Projet créé avec succès",
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error('Project save error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de l'enregistrement",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              {onBack && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={onBack}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour
                </Button>
              )}
              <CardTitle className="text-2xl font-black flex-1">
                {project ? "Modifier le projet" : "Nouveau projet"}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Titre *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => {
                      setFormData({ ...formData, title: e.target.value });
                      if (validationErrors.title) {
                        setValidationErrors(prev => ({ ...prev, title: '' }));
                      }
                    }}
                    required
                    maxLength={100}
                    className={validationErrors.title ? "border-destructive" : ""}
                  />
                  {validationErrors.title && (
                    <p className="text-sm text-destructive">{validationErrors.title}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Couleur</label>
                  <Select value={formData.color} onValueChange={(value: 'yellow' | 'blue' | 'green' | 'pink') => 
                    setFormData({ ...formData, color: value })
                  }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yellow">Jaune</SelectItem>
                      <SelectItem value="blue">Bleu</SelectItem>
                      <SelectItem value="green">Vert</SelectItem>
                      <SelectItem value="pink">Rose</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description courte *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    if (validationErrors.description) {
                      setValidationErrors(prev => ({ ...prev, description: '' }));
                    }
                  }}
                  rows={3}
                  required
                  maxLength={500}
                  className={validationErrors.description ? "border-destructive" : ""}
                />
                {validationErrors.description && (
                  <p className="text-sm text-destructive">{validationErrors.description}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.description.length}/500 caractères
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description longue</label>
                <Textarea
                  value={formData.long_description}
                  onChange={(e) => setFormData({ ...formData, long_description: e.target.value })}
                  rows={5}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">URL de démo</label>
                  <Input
                    type="url"
                    value={formData.demo_url}
                    onChange={(e) => setFormData({ ...formData, demo_url: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">URL GitHub</label>
                  <Input
                    type="url"
                    value={formData.github_url}
                    onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                  />
                </div>
              </div>

              <ImageUpload
                label="Image principale"
                currentImage={formData.image}
                onUpload={handleImageUpload('main')}
                onRemove={handleImageRemove('main')}
              />

              <div className="space-y-4">
                <label className="text-sm font-medium">Type d'affichage</label>
                <div className="flex gap-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="display_type"
                      value="carousel"
                      checked={formData.display_type === 'carousel'}
                      onChange={(e) => setFormData({ ...formData, display_type: e.target.value as 'carousel' | 'before_after' | 'video' })}
                      className="w-4 h-4 text-primary bg-background border-border focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Carrousel</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="display_type"
                      value="before_after"
                      checked={formData.display_type === 'before_after'}
                      onChange={(e) => setFormData({ ...formData, display_type: e.target.value as 'carousel' | 'before_after' | 'video' })}
                      className="w-4 h-4 text-primary bg-background border-border focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Avant/Après</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="display_type"
                      value="video"
                      checked={formData.display_type === 'video'}
                      onChange={(e) => setFormData({ ...formData, display_type: e.target.value as 'carousel' | 'before_after' | 'video' })}
                      className="w-4 h-4 text-primary bg-background border-border focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Vidéo</span>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-sm font-medium">Tags</label>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Nouveau tag"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" onClick={addTag} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>

              {formData.display_type === 'carousel' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Images d'aperçu</label>
                    <ImageUpload
                      label=""
                      onUpload={handleImageUpload('preview')}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {formData.preview_images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={image} 
                          alt={`Aperçu ${index + 1}`}
                          className="w-full h-24 object-cover rounded border border-border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removePreviewImage(image)}
                          className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.display_type === 'before_after' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ImageUpload
                    label="Image 'Avant'"
                    currentImage={formData.before_after_before}
                    onUpload={handleImageUpload('before')}
                    onRemove={handleImageRemove('before')}
                  />

                  <ImageUpload
                    label="Image 'Après'"
                    currentImage={formData.before_after_after}
                    onUpload={handleImageUpload('after')}
                    onRemove={handleImageRemove('after')}
                  />
                </div>
              )}

              {formData.display_type === 'video' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">URL de la vidéo YouTube *</label>
                    <Input
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => {
                        setFormData({ ...formData, video_url: e.target.value });
                        if (validationErrors.video_url) {
                          setValidationErrors(prev => ({ ...prev, video_url: '' }));
                        }
                      }}
                      placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                      className={`w-full ${validationErrors.video_url ? "border-destructive" : ""}`}
                    />
                    {validationErrors.video_url && (
                      <p className="text-sm text-destructive">{validationErrors.video_url}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      <strong>Conseils pour optimiser :</strong><br/>
                      • Utilisez des URLs YouTube pour une meilleure performance<br/>
                      • Évitez d'uploader des fichiers vidéo volumineux directement<br/>
                      • Les vidéos YouTube se chargent de manière optimisée<br/>
                      • Format recommandé: https://www.youtube.com/watch?v=VIDEO_ID
                    </p>
                  </div>
                  
                  {formData.video_url && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Aperçu</label>
                      <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                        <p className="text-muted-foreground">Aperçu disponible après enregistrement</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_school_project}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, is_school_project: checked })
                    }
                  />
                  <label className="text-sm font-medium">Projet d'école</label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, is_visible: checked })
                    }
                  />
                  <label className="text-sm font-medium">Visible</label>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Ordre d'affichage</label>
                <Input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Enregistrement..." : "Enregistrer"}
                </Button>
                <Button type="button" variant="outline" onClick={onCancel}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectForm;